// src/components/loginpage/signupbox/signupvalidator.js
// Pure validation. Returns all issues; does not call notify itself.
//
// Password policy (must match the backend contract exactly):
//   - 12 to 20 characters
//   - at least 1 uppercase letter
//   - at least 1 lowercase letter
//   - at least 1 number
//   - at least 1 special character
// Both the live checklist and this submit-time check read PASSWORD_RULES,
// so they can never disagree.

const hasOnlyLetters = (s) => /^[A-Za-z]+$/.test(s);
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
const countMatches = (s, re) => (s.match(re) || []).length;

// Field length caps — mirror the backend contract.
const MAX_NAME = 100;      // susenf / susenl
const MAX_USERNAME = 100;  // susern
const MAX_EMAIL = 320;     // suseremail

// ── Password rules — single source of truth ─────────────────────────
export const PASSWORD_RULES = [
  {
    id: 'length',
    label: '12–20 characters long',
    test: (pw) => pw.length >= 12 && pw.length <= 20,
  },
  {
    id: 'uppercase',
    label: 'At least 1 uppercase letter',
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    id: 'lowercase',
    label: 'At least 1 lowercase letter',
    test: (pw) => /[a-z]/.test(pw),
  },
  {
    id: 'number',
    label: 'At least 1 number',
    test: (pw) => /[0-9]/.test(pw),
  },
  {
    id: 'special',
    label: 'At least 1 special character',
    test: (pw) => countMatches(pw, /[^A-Za-z0-9]/g) >= 1,
  },
];

// True only if every password rule passes.
export const isPasswordValid = (pw) => PASSWORD_RULES.every((r) => r.test(pw || ''));

export function runSignupValidation(getById) {
  const $ = getById || ((id) => document.getElementById(id));

  const values = {
    firstName:  $('signup-first')?.value?.trim()   || '',
    secondName: $('signup-second')?.value?.trim()  || '',
    country:    $('signup-country')?.value?.trim() || '',
    address:    $('signup-address')?.value?.trim() || '',
    username:   $('signup-username')?.value?.trim()|| '',
    email:      $('signup-email')?.value?.trim()   || '',
    password:   $('signup-password')?.value        || '',
  };

  const errors = [];

  // Required (all fields)
  if (!values.firstName)  errors.push('First Name: required.');
  if (!values.secondName) errors.push('Second Name: required.');
  if (!values.country)    errors.push('Country: required.');
  if (!values.address)    errors.push('Address: required.');
  if (!values.username)   errors.push('Username: required.');
  if (!values.email)      errors.push('Email Address: required.');
  if (!values.password)   errors.push('Password: required.');

  // First Name rules
  if (values.firstName) {
    if (!hasOnlyLetters(values.firstName)) errors.push('First Name: letters only.');
    if (values.firstName.length > MAX_NAME) errors.push(`First Name: must be ${MAX_NAME} characters or fewer.`);
  }

  // Second Name rules
  if (values.secondName) {
    if (!hasOnlyLetters(values.secondName)) errors.push('Second Name: letters only.');
    if (values.secondName.length > MAX_NAME) errors.push(`Second Name: must be ${MAX_NAME} characters or fewer.`);
  }

  // Username rules
  if (values.username && values.username.length > MAX_USERNAME) {
    errors.push(`Username: must be ${MAX_USERNAME} characters or fewer.`);
  }

  // Email rules
  if (values.email) {
    if (!looksLikeEmail(values.email)) {
      errors.push('Email Address: must be a valid email (name@domain.tld).');
    } else if (values.email.length > MAX_EMAIL) {
      errors.push(`Email Address: must be ${MAX_EMAIL} characters or fewer.`);
    }
  }

  // Password rules — derived from PASSWORD_RULES so the live checklist and
  // this submit-time check always match.
  if (values.password) {
    const failed = PASSWORD_RULES.filter((r) => !r.test(values.password));
    if (failed.length) {
      const pwIssues = failed.map((r) => `• ${r.label}`);
      errors.push(`Password:\n${pwIssues.join('\n')}`);
    }
  }

  if (errors.length) {
    return { ok: false, errors, values };
  }

  return { ok: true, errors: [], values };
}